/*
  Dropdown menus in the category bar.

  Each menu is a <details>, so it already opens on tap, click and keyboard
  with no script. This adds what <details> cannot do on its own:

  - lines the panel up under its item. The panel is positioned against the
    whole bar, not the item, so the sideways-scrolling row cannot clip it
    (see section-category-bar.css), which means its offset has to be set here;
  - opens on hover where there is a real pointer, and keeps a click from
    closing a menu that hover has just opened;
  - closes on a click outside, on Escape, when focus leaves, and when the row
    is scrolled or the window resized, since either moves the item out from
    under its panel.

  It also drives the row's scroll arrows (initArrows, below).
*/
(() => {
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const CLOSE_DELAY = 150;

  const position = (menu) => {
    const bar = menu.closest('.category-bar');
    const panel = menu.querySelector('.category-bar__panel');
    const summary = menu.querySelector('summary');
    if (!bar || !panel || !summary) return;

    const barBox = bar.getBoundingClientRect();
    let left = summary.getBoundingClientRect().left - barBox.left;
    // Keep the panel inside the bar when the item sits near its right edge.
    const overflow = left + panel.offsetWidth - (barBox.width - 8);
    if (overflow > 0) left -= overflow;
    panel.style.setProperty('--category-menu-left', `${Math.max(8, Math.round(left))}px`);
    // The panel stays invisible until this has run (section-category-bar.css).
    menu.dataset.positioned = 'true';
  };

  const close = (menu, focus) => {
    if (!menu.open) return;
    menu.open = false;
    if (focus) menu.querySelector('summary').focus();
  };

  const closeAll = (except) => {
    document.querySelectorAll('details[data-category-menu][open]').forEach((menu) => {
      if (menu !== except) close(menu);
    });
  };

  const init = (menu) => {
    if (menu.dataset.categoryMenuReady) return;
    menu.dataset.categoryMenuReady = 'true';

    const item = menu.parentElement;
    const summary = menu.querySelector('summary');
    let closeTimer = null;
    let openedByHover = false;

    menu.addEventListener('toggle', () => {
      if (menu.open) {
        closeAll(menu);
        position(menu);
      } else {
        openedByHover = false;
        // The row may scroll or the window resize before the next open, so
        // the next one measures again rather than trusting the old offset.
        delete menu.dataset.positioned;
      }
    });

    item.addEventListener('mouseenter', () => {
      if (!canHover.matches) return;
      clearTimeout(closeTimer);
      if (!menu.open) {
        openedByHover = true;
        menu.open = true;
        // Position in the same task as opening, so the first paint already
        // has the panel under its item. Waiting for the toggle event, which
        // fires a frame later, showed it for that frame at the bar's left
        // edge -- the fallback offset -- on the first hover after a load.
        position(menu);
      }
    });

    item.addEventListener('mouseleave', () => {
      if (!canHover.matches || !openedByHover) return;
      closeTimer = setTimeout(() => close(menu), CLOSE_DELAY);
    });

    // With a mouse, the pointer is already over the item when it clicks, so
    // hover has opened the menu and the click would only shut it again. A
    // keyboard press arrives as a click with detail 0 and still toggles.
    summary.addEventListener('click', (event) => {
      if (event.detail === 0 || !openedByHover) return;
      event.preventDefault();
      openedByHover = false;
    });

    menu.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') close(menu, true);
    });

    menu.addEventListener('focusout', (event) => {
      if (!menu.contains(event.relatedTarget)) close(menu);
    });

    const row = menu.closest('.category-bar__inner');
    if (row) row.addEventListener('scroll', () => close(menu), { passive: true });
  };

  const initAll = () => document.querySelectorAll('details[data-category-menu]').forEach(init);

  document.addEventListener('click', (event) => {
    if (!event.target.closest || !event.target.closest('details[data-category-menu]')) closeAll();
  });
  window.addEventListener('resize', () => closeAll());

  /*
    Scroll arrows. Each is shown only while the row has more to reveal in its
    direction, so they double as the hint that the row scrolls at all, and a
    tap moves it most of a screen. The 2px slack absorbs the fractional
    scroll positions phones report at either end.
  */
  const initArrows = (bar) => {
    if (bar.dataset.categoryArrowsReady) return;
    const row = bar.querySelector('.category-bar__inner');
    const prev = bar.querySelector('[data-category-scroll="-1"]');
    const next = bar.querySelector('[data-category-scroll="1"]');
    if (!row || !prev || !next) return;
    bar.dataset.categoryArrowsReady = 'true';

    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      prev.hidden = row.scrollLeft <= 2;
      next.hidden = row.scrollLeft >= max - 2;
    };

    [prev, next].forEach((button) =>
      button.addEventListener('click', () => {
        const direction = Number(button.dataset.categoryScroll);
        row.scrollBy({ left: direction * row.clientWidth * 0.65, behavior: 'smooth' });
      })
    );

    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // Web fonts can change the row's width after first paint.
    if (document.fonts) document.fonts.ready.then(update);
    update();
  };

  const initAllArrows = () => document.querySelectorAll('.category-bar').forEach(initArrows);

  initAll();
  initAllArrows();
  document.addEventListener('shopify:section:load', () => {
    initAll();
    initAllArrows();
  });
})();
